import { useQuery, useSubscription } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { MY_LEAVE_LEAVES_SUBSCRIPTION } from "my-leave/graphql";
import { useReqestLeavesData } from "my-leave/hooks";
import { useOcPolling } from "oc/hooks";

const QUERY = gql`
  query leaveYearlyDaysByContract($contract: String!, $year: String!) {
    leaveYearlyDaysByContract(contract: $contract, year: $year) {
      _id
      year
      availableLeaveDays
      availableStudyLeaveDays
      availableESZJTV19LeaveDays
      availableCovidPlus10LeaveDays
      availableEmergencyLeaveDays
      fulfilledLeaveDays
      fulfilledStudyLeaveDays
      fulfilledAbsenceDays
      fulfilledCovidPlus10Days
      fulfilledESZJTV19Days
      fulfilledEmergencyDays
    }
  }
`;

export const useMyLeaveSummaryChartData = ({
  year,
  contract,
}: {
  year?: string;
  contract: string;
}) => {
  if (!year) {
    year = new Date().getFullYear().toString();
  }

  const { data, loading, error, refetch } = useQuery(QUERY, {
    skip: !contract || !year,
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: {
      contract: contract,
      year: year,
    },
  });

  useSubscription(MY_LEAVE_LEAVES_SUBSCRIPTION, {
    fetchPolicy: "network-only",
    skip: !contract,
    onData: () => {
      refetch();
    },
  });

  const { requestedLeaveDays: requestedLeaveDaysState } = useReqestLeavesData({
    selectedYear: year,
    contractCode: contract,
  });

  let result = data;

  type LeaveYearlyDaysByContract = {
    _id: string;
    year: string;
    availableLeaveDays: number;
    availableStudyLeaveDays: number;
    availableESZJTV19LeaveDays: number;
    availableCovidPlus10LeaveDays: number;
    availableEmergencyLeaveDays: number;
    fulfilledLeaveDays: number;
    fulfilledStudyLeaveDays: number;
    fulfilledAbsenceDays: number;
    fulfilledCovidPlus10Days: number;
    fulfilledESZJTV19Days: number;
    fulfilledEmergencyDays: number;
  };

  let myFulfilledLeaveDays: LeaveYearlyDaysByContract[] =
    result?.leaveYearlyDaysByContract || [];

  let filteredByYear = myFulfilledLeaveDays?.find(
    (item: LeaveYearlyDaysByContract) => item.year === year,
  );

  const fulfilledCovidPlus10Days =
    filteredByYear?.fulfilledCovidPlus10Days || 0;

  const requestedCovidPlus10LeaveDays = requestedLeaveDaysState?.covid;

  const availableCovidPlus10LeaveDays =
    (filteredByYear?.availableCovidPlus10LeaveDays ?? 0) -
    (requestedCovidPlus10LeaveDays ?? 0);

  const allCovidPlus10LeaveDays =
    availableCovidPlus10LeaveDays +
    fulfilledCovidPlus10Days +
    requestedCovidPlus10LeaveDays;

  const fulfilledLeaveDays = filteredByYear?.fulfilledLeaveDays || 0;

  const requestedLeaveDays = requestedLeaveDaysState?.base;

  const availableLeaveDays =
    (filteredByYear?.availableLeaveDays ?? 0) - (requestedLeaveDays ?? 0);

  const allLeaveDays =
    availableLeaveDays + fulfilledLeaveDays + requestedLeaveDays;

  const fulfilledStudyLeaveDays = filteredByYear?.fulfilledStudyLeaveDays || 0;

  const requestedStudyLeaveDays = requestedLeaveDaysState?.study;

  const availableStudyLeaveDays =
    (filteredByYear?.availableStudyLeaveDays ?? 0) -
    (requestedStudyLeaveDays ?? 0);

  const allStudyLeaveDays =
    availableStudyLeaveDays + fulfilledStudyLeaveDays + requestedStudyLeaveDays;

  const fulfilledESZJTV19Days = filteredByYear?.fulfilledESZJTV19Days || 0;

  const requestedESZJTV19LeaveDays = requestedLeaveDaysState?.eszjtv;

  const availableESZJTV19LeaveDays =
    (filteredByYear?.availableESZJTV19LeaveDays ?? 0) -
    (requestedESZJTV19LeaveDays ?? 0);

  const allESZJTV19LeaveDays =
    availableESZJTV19LeaveDays +
    fulfilledESZJTV19Days +
    requestedESZJTV19LeaveDays;

  const requestedEmergencyLeaveDays = requestedLeaveDaysState?.emergency;
  const fulfilledEmergencyDays = filteredByYear?.fulfilledEmergencyDays ?? 0;
  const availableEmergencyLeaveDays =
    (filteredByYear?.availableEmergencyLeaveDays ?? 0) -
    (requestedEmergencyLeaveDays ?? 0);

  const allEmergencyLeaveDays =
    availableEmergencyLeaveDays +
    fulfilledEmergencyDays +
    requestedEmergencyLeaveDays;

  const currentLeaveYear = result?.leaveYearlyDaysByContract?.filter(
    (item: LeaveYearlyDaysByContract) => item?.year === year,
  );
  return {
    data: {
      year,
      availableLeaveDays,
      fulfilledLeaveDays,
      requestedLeaveDays,
      allLeaveDays,
      fulfilledCovidPlus10Days,
      allCovidPlus10LeaveDays,
      requestedCovidPlus10LeaveDays,
      availableCovidPlus10LeaveDays,
      fulfilledStudyLeaveDays,
      availableStudyLeaveDays,
      requestedStudyLeaveDays,
      allStudyLeaveDays,
      fulfilledESZJTV19Days,
      availableESZJTV19LeaveDays,
      requestedESZJTV19LeaveDays,
      allESZJTV19LeaveDays,
      fulfilledEmergencyDays,
      availableEmergencyLeaveDays,
      requestedEmergencyLeaveDays,
      allEmergencyLeaveDays,
    },
    loading,
    error,
    noLeave:
      result?.leaveYearlyDaysByContract?.length === undefined ||
      result?.leaveYearlyDaysByContract?.length === 0 ||
      currentLeaveYear?.length === 0
        ? true
        : false,
  };
};

export default useMyLeaveSummaryChartData;
